import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth-store';
import { TableSkeleton } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { documentHref } from '../../api/media';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { Symbol } from '../../components/symbol';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useUrlTab } from '../../services/attention';

const PER_PAGE = 15;

// ---------- Types (mirror the admin-seller serialized shapes) ----------------

interface SellerRow {
  id: string;
  businessName: string;
  email: string;
  phoneE164: string;
  gstin: string | null;
  pan: string | null;
  /** Last 4 digits only — the number itself is stored encrypted server-side. */
  aadhaarLast4: string | null;
  tradeLicenseNo: string | null;
  kycStatus: string;
  onboardingStatus: string;
  isActive: boolean;
  /** Reason given when the application was last rejected; cleared on resubmission. */
  rejectionReason?: string | null;
  createdAt: string;
}

interface SellerDocument {
  id: string;
  docType: string;
  fileUrl: string;
  verified: boolean;
  verifiedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

interface SellerAddress {
  id: string;
  addressType: string;
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  pincode: string;
}

interface SellerBankAccount {
  id: string;
  accountHolderName: string;
  accountNumberLast4: string;
  ifsc: string;
  bankName: string | null;
  isPrimary: boolean;
  verificationStatus: string;
}

interface SellerDetail extends SellerRow {
  legalName: string | null;
  ownerName: string;
  businessType: string | null;
  addresses: SellerAddress[];
  documents: SellerDocument[];
  bankAccounts: SellerBankAccount[];
}

const STATUS_TABS = ['SUBMITTED', 'DOCS_PENDING', 'APPROVED', 'REJECTED', 'ALL'] as const;
type StatusTab = (typeof STATUS_TABS)[number];

// ---------- Page -------------------------------------------------------------

export function SellersPage() {
  const hasScope = useAuthStore((s) => s.hasScope);
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [tab, setTab] = useUrlTab<StatusTab>(STATUS_TABS, 'SUBMITTED');
  const [q, setQ] = useState('');
  // Server-searched: the list is paged by the API, so the query goes there and
  // matches every column below (business, GSTIN, email, phone, KYC, onboarding,
  // active).
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [rows, setRows] = useState<SellerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: PER_PAGE };
      if (tab !== 'ALL') params.status = tab;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get<{ data: SellerRow[]; pagination: { total: number } }>('/admin/sellers', { params });
      if (requestId !== latestRequest.current) return;
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Back to page 1 whenever the tab or search changes, so a search made on
  // page 4 does not silently show nothing.
  useEffect(() => { setPage(1); }, [tab, debouncedQ]);
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, debouncedQ, page]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const searching = debouncedQ.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sellers"
        description="Review KYC applications and manage seller accounts."
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className="fchip"
          >
            {t === 'SUBMITTED' ? 'KYC queue' : t === 'DOCS_PENDING' ? 'Docs to review' : t.charAt(0) + t.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <TableSearch
        value={q}
        onChange={setQ}
        placeholder="Search by business, GSTIN, email, phone, KYC or status"
        resultCount={!loading && searching ? total : undefined}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <TableSkeleton cols={6} />
      ) : rows.length === 0 && searching ? (
        <EmptyState
          icon="search_off"
          title={`No matches for “${debouncedQ}”`}
          body="Try another word, or clear the search to see every seller in this view."
          action={<button type="button" onClick={() => setQ('')} className="btn btn-secondary">Clear search</button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="storefront"
          title="No sellers in this view"
          body={tab === 'SUBMITTED'
            ? 'Sellers appear here once they submit their KYC documents for review.'
            : 'Try another status or clear the search.'}
        />
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Contact</th>
                  <th>KYC</th>
                  <th>Onboarding</th>
                  <th>Active</th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="font-semibold text-ink-900">{s.businessName}</div>
                      {s.gstin
                        ? <div className="sku text-ink-500">{s.gstin}</div>
                        : <div className="text-[13px] text-ink-500">No GSTIN</div>}
                    </td>
                    <td>
                      <div className="text-ink-700">{s.email}</div>
                      <div className="text-[13px] text-ink-500">{s.phoneE164}</div>
                    </td>
                    <td><StatusPill value={s.kycStatus} /></td>
                    <td><StatusPill value={s.onboardingStatus} /></td>
                    <td>
                      {s.isActive
                        ? <span className="badge badge-success badge-dot">Active</span>
                        : <span className="badge">No</span>}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedId(s.id)}
                        className="btn btn-ghost btn-sm"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={total}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}

      {selectedId && (
        <SellerDetailDrawer
          sellerId={selectedId}
          canApprove={hasScope('seller.kyc.approve')}
          canSuspend={hasScope('seller.suspend')}
          onClose={() => setSelectedId(null)}
          onChanged={() => { void load(); }}
        />
      )}
    </div>
  );
}

// ---------- Detail drawer ----------------------------------------------------

function SellerDetailDrawer(props: {
  sellerId: string;
  canApprove: boolean;
  canSuspend: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<SellerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirm, prompt } = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<SellerDetail>(`/admin/sellers/${props.sellerId}`);
      setDetail(data);
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [props.sellerId]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      props.onChanged();
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyDoc = async (doc: SellerDocument, verified: boolean) => {
    let rejectionReason: string | null = null;
    if (!verified) {
      rejectionReason = await prompt({
        title: 'Reject document?',
        description: `The seller sees your reason and has to upload their ${sentence(doc.docType)} again. The decision is locked once saved.`,
        label: 'Reason for rejection',
        placeholder: 'e.g. The photo is blurred; the name does not match',
        required: true,
        multiline: true,
        confirmLabel: 'Reject document',
        tone: 'danger',
      });
      if (rejectionReason === null) return;
    }
    void act(() => api.post(`/admin/sellers/${props.sellerId}/documents/${doc.id}/verify`, { verified, rejectionReason }));
  };

  const approve = () => act(() => api.post(`/admin/sellers/${props.sellerId}/approve`));
  const reject = async () => {
    const reason = await prompt({
      title: 'Reject seller?',
      description: `${detail?.businessName ?? 'The seller'} is told why and can fix their details and resubmit for review.`,
      label: 'Reason for rejection',
      placeholder: 'e.g. GSTIN does not match the business name',
      required: true,
      multiline: true,
      confirmLabel: 'Reject seller',
      tone: 'danger',
    });
    if (reason === null) return;
    void act(() => api.post(`/admin/sellers/${props.sellerId}/reject`, { reason }));
  };
  // Suspending hides every product, blocks new orders and signs the seller out
  // everywhere; orders already placed stay open for fulfilment or cancellation.
  const setActive = async (active: boolean) => {
    if (active) {
      const ok = await confirm({
        title: 'Reactivate seller?',
        description: 'Their products become visible to shoppers again and they can take new orders.',
        confirmLabel: 'Reactivate seller',
        tone: 'primary',
      });
      if (!ok) return;
      void act(() => api.post(`/admin/sellers/${props.sellerId}/reactivate`));
      return;
    }
    const reason = await prompt({
      title: 'Suspend seller?',
      description:
        'Their products are hidden, new orders are blocked and they are signed out everywhere. ' +
        'Orders already placed are not cancelled — handle them from the Orders page. You can reactivate the seller later.',
      label: 'Reason (sent to the seller)',
      placeholder: 'e.g. Repeated late dispatches',
      required: false,
      multiline: true,
      confirmLabel: 'Suspend seller',
      tone: 'danger',
    });
    if (reason === null) return;
    void act(() =>
      api.post(`/admin/sellers/${props.sellerId}/suspend`, reason.trim() ? { reason: reason.trim() } : {}),
    );
  };

  const showApproveActions = props.canApprove && detail?.onboardingStatus === 'SUBMITTED';
  const showSuspendActions = props.canSuspend && detail?.onboardingStatus === 'APPROVED';

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-scrim" onClick={props.onClose}>
      <div
        className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <h2 className="font-display text-drawer-title text-ink-900">Seller review</h2>
          <button type="button" onClick={props.onClose} aria-label="Close" className="btn btn-quiet btn-icon btn-sm -mr-2">
            <Symbol name="close" size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {error && <div className="mb-5"><ErrorBanner message={error} onDismiss={() => setError(null)} /></div>}

          {loading || !detail ? (
            <p className="text-[14px] text-ink-500">Loading…</p>
          ) : (
            <div className="space-y-6">
              <section>
                <h3 className="text-[19px] font-semibold leading-tight text-ink-900">{detail.businessName}</h3>
                <p className="mt-0.5 text-[13.5px] text-ink-500">{detail.legalName ?? '—'} · {detail.businessType ? sentence(detail.businessType) : '—'}</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <StatusPill value={detail.kycStatus} />
                  <StatusPill value={detail.onboardingStatus} />
                  {detail.isActive && <span className="badge badge-success badge-dot">Active</span>}
                  {!detail.isActive && detail.onboardingStatus === 'APPROVED' && (
                    <span className="badge badge-danger badge-dot">Suspended</span>
                  )}
                </div>
                {detail.onboardingStatus === 'REJECTED' && detail.rejectionReason && (
                  <div className="alert alert-danger mt-3 text-[13.5px]">Rejected: {detail.rejectionReason}</div>
                )}
                <dl className="card mt-4 grid grid-cols-2 gap-x-6 gap-y-3 p-4 text-[14px]">
                  <Detail label="Owner" value={detail.ownerName} />
                  <Detail label="Email" value={detail.email} />
                  <Detail label="Phone" value={detail.phoneE164} />
                  {/* Aadhaar is the mandatory proof; GSTIN / PAN / trade licence
                      are optional. Only the last 4 digits ever leave the API —
                      verify the full number against the uploaded Aadhaar card. */}
                  <Detail
                    label="Aadhaar"
                    value={detail.aadhaarLast4 ? `•••• •••• ${detail.aadhaarLast4}` : 'Not provided'}
                    mono={Boolean(detail.aadhaarLast4)}
                  />
                  <Detail label="GSTIN" value={detail.gstin ?? '—'} mono={Boolean(detail.gstin)} />
                  <Detail label="PAN" value={detail.pan ?? '—'} mono={Boolean(detail.pan)} />
                  <Detail label="Trade licence" value={detail.tradeLicenseNo ?? '—'} mono={Boolean(detail.tradeLicenseNo)} />
                </dl>
              </section>

              <section>
                <h4 className="mb-2.5 text-[14.5px] font-semibold text-ink-900">KYC documents</h4>
                {detail.documents.length === 0 ? (
                  <p className="text-[13.5px] text-ink-500">No documents uploaded yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.documents.map((d) => (
                      <li key={d.id} className="card flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <a href={documentHref(d.fileUrl)} target="_blank" rel="noreferrer" title="Private link, valid for 10 minutes — reopen the seller to refresh" className="text-[14px] font-semibold text-admin-ink hover:underline">{sentence(d.docType)}</a>
                          <div className="mt-1">
                            {d.verified
                              ? <span className="badge badge-success">Verified</span>
                              : d.rejectionReason
                                ? <span className="text-[13px] text-danger-ink">Rejected: {d.rejectionReason}</span>
                                : <span className="badge badge-warning">Pending</span>}
                          </div>
                        </div>
                        {props.canApprove && (() => {
                          // Once a document is decided (verified or rejected) both
                          // actions lock — no re-verifying an already-actioned doc.
                          const decided = d.verified || Boolean(d.rejectionReason);
                          return (
                            <div className="flex shrink-0 gap-1.5">
                              <button type="button" disabled={busy || decided} onClick={() => void verifyDoc(d, true)} className="btn btn-tonal btn-sm">Verify</button>
                              <button type="button" disabled={busy || decided} onClick={() => void verifyDoc(d, false)} className="btn btn-danger-outline btn-sm">Reject</button>
                            </div>
                          );
                        })()}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="mb-2.5 text-[14.5px] font-semibold text-ink-900">Bank accounts</h4>
                {detail.bankAccounts.length === 0 ? (
                  <p className="text-[13.5px] text-ink-500">No bank account added yet.</p>
                ) : (
                  <ul className="space-y-2 text-[14px]">
                    {detail.bankAccounts.map((b) => (
                      <li key={b.id} className="card flex items-center justify-between gap-3 px-4 py-3">
                        <span className="min-w-0 text-ink-900">
                          {b.accountHolderName} · <span className="sku">••••{b.accountNumberLast4}</span> · <span className="sku">{b.ifsc}</span>
                        </span>
                        <StatusPill value={b.verificationStatus} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="mb-2.5 text-[14.5px] font-semibold text-ink-900">Addresses</h4>
                {detail.addresses.length === 0 ? (
                  <p className="text-[13.5px] text-ink-500">No addresses added yet.</p>
                ) : (
                  <ul className="space-y-1.5 text-[14px] text-ink-700">
                    {detail.addresses.map((a) => (
                      <li key={a.id}>
                        <span className="font-semibold text-ink-500">{sentence(a.addressType)}: </span>
                        {a.line1}{a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} {a.pincode}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </div>

        {detail && !loading && (showApproveActions || showSuspendActions) && (
          <div className="flex shrink-0 justify-end gap-2.5 border-t border-surface-line px-6 py-3.5">
            {showApproveActions && (
              <>
                <button type="button" disabled={busy} onClick={() => void reject()} className="btn btn-danger-outline">
                  Reject
                </button>
                <button type="button" disabled={busy} onClick={() => void approve()} className="btn btn-primary">
                  {busy ? 'Working…' : 'Approve seller'}
                </button>
              </>
            )}
            {showSuspendActions && (
              detail.isActive
                ? <button type="button" disabled={busy} onClick={() => void setActive(false)} className="btn btn-danger-outline">Suspend</button>
                : <button type="button" disabled={busy} onClick={() => void setActive(true)} className="btn btn-primary">Reactivate</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Helpers ----------------------------------------------------------

/** Display-only sentence case for API enums: `PENDING_REVIEW` → "Pending review". */
function sentence(value: string): string {
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function StatusPill({ value }: { value: string }) {
  const tone =
    ['APPROVED', 'VERIFIED', 'ACTIVE'].includes(value) ? 'badge-success'
      : ['REJECTED', 'FAILED'].includes(value) ? 'badge-danger'
        : ['SUBMITTED', 'PENDING', 'PENDING_REVIEW'].includes(value) ? 'badge-warning'
          : '';
  return <span className={`badge ${tone}`}>{sentence(value)}</span>;
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12.5px] text-ink-500">{label}</dt>
      <dd className={`break-words text-ink-900 ${mono ? 'sku' : ''}`}>{value}</dd>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div role="alert" className="alert alert-danger items-center justify-between">
      <span>{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="btn btn-quiet btn-icon btn-sm -my-1 text-sindoor-700">
          <Symbol name="close" size={18} />
        </button>
      )}
    </div>
  );
}
